import React, { useState } from 'react';
import { BarChart2, ArrowDownUp, Maximize2 } from 'lucide-react';
import { UnitCategory } from '../types/units';
import { convertValue } from '../utils/conversions';
import { unitsByCategory } from '../utils/units';

const descriptions = {
  comparison: {
    title: "Unit Comparison",
    description: "Compare a single value across all units in this category. Visualize the relationships between different units with an interactive chart."
  },
  range: {
    title: "Range Generator",
    description: "Create conversion tables across a range of values. Perfect for creating reference charts, analyzing trends, or educational purposes."
  }
};

interface UnitComparisonProps {
  category: UnitCategory;
}

export const UnitComparison: React.FC<UnitComparisonProps> = ({ category }) => {
  const [baseValue, setBaseValue] = useState('');
  const [baseUnit, setBaseUnit] = useState('');
  const [comparisonResults, setComparisonResults] = useState<{
    unit: string;
    value: number;
    percentage: number;
  }[]>([]);

  const [rangeStart, setRangeStart] = useState('');
  const [rangeEnd, setRangeEnd] = useState('');
  const [rangeUnit, setRangeUnit] = useState('');
  const [rangeSteps, setRangeSteps] = useState('');
  const [rangeResults, setRangeResults] = useState<{
    value: number;
    conversions: { unit: string; value: number }[];
  }[]>([]);

  const compareUnits = (e: React.FormEvent) => {
    e.preventDefault();
    if (!baseValue || !baseUnit) return;

    const value = Number(baseValue);
    const results = unitsByCategory[category]
      .map(unit => {
        const converted = convertValue(value, baseUnit, unit.value, category);
        return {
          unit: unit.value,
          value: converted,
          percentage: (converted / value) * 100
        };
      })
      .sort((a, b) => b.value - a.value);

    setComparisonResults(results);
  };

  const generateRange = (e: React.FormEvent) => {
    e.preventDefault();
    if (!rangeStart || !rangeEnd || !rangeUnit || !rangeSteps) return;

    const start = Number(rangeStart);
    const end = Number(rangeEnd);
    const steps = Number(rangeSteps);
    const stepSize = (end - start) / (steps - 1);

    const results = Array.from({ length: steps }, (_, i) => {
      const value = start + (stepSize * i);
      return {
        value,
        conversions: unitsByCategory[category].map(unit => ({
          unit: unit.value,
          value: convertValue(value, rangeUnit, unit.value, category)
        }))
      };
    });

    setRangeResults(results);
  };

  return (
    <div className="mt-8 space-y-6">
      <div className="flex items-center gap-2">
        <Maximize2 className="w-5 h-5 text-blue-600" />
        <h2 className="text-lg font-semibold text-gray-800">Advanced Unit Tools</h2>
      </div>

      {/* Unit Comparison */}
      <div className="p-6 bg-white/80 backdrop-blur rounded-xl border border-gray-100 shadow-lg">
        <div className="flex items-center gap-2 mb-4">
          <BarChart2 className="w-5 h-5 text-blue-600" />
          <div>
            <h2 className="text-lg font-semibold text-gray-800">{descriptions.comparison.title}</h2>
            <p className="text-gray-600 text-sm mt-1">{descriptions.comparison.description}</p>
          </div>
        </div>

        <form onSubmit={compareUnits} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4">
            <input
              type="number"
              value={baseValue}
              onChange={(e) => setBaseValue(e.target.value)}
              placeholder="Enter value"
              className="w-full px-4 py-2 border border-gray-200 rounded-lg"
            />
            <select
              value={baseUnit}
              onChange={(e) => setBaseUnit(e.target.value)}
              className="w-full px-4 py-2 border border-gray-200 rounded-lg"
            >
              <option value="">Select unit</option>
              {unitsByCategory[category].map(unit => (
                <option key={unit.value} value={unit.value}>
                  {unit.label}
                </option>
              ))}
            </select>
          </div>
          <button
            type="submit"
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Compare All Units
          </button>
        </form>

        {comparisonResults.length > 0 && (
          <div className="mt-6 space-y-3">
            {comparisonResults.map((result, index) => (
              <div key={index} className="relative">
                <div className="flex justify-between text-sm mb-1">
                  <span>{result.unit}</span>
                  <span>{result.value.toFixed(4)}</span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-blue-500 rounded-full"
                    style={{ width: `${Math.min(100, result.percentage)}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Range Generator */}
      <div className="p-6 bg-white/80 backdrop-blur rounded-xl border border-gray-100 shadow-lg">
        <div className="flex items-center gap-2 mb-4">
          <ArrowDownUp className="w-5 h-5 text-blue-600" />
          <div className="space-y-1">
            <h2 className="text-lg font-semibold text-gray-800">{descriptions.range.title}</h2>
            <p className="text-gray-600 text-sm">{descriptions.range.description}</p>
          </div>
        </div>

        <form onSubmit={generateRange} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4">
            <input
              type="number"
              value={rangeStart}
              onChange={(e) => setRangeStart(e.target.value)}
              placeholder="Start value"
              className="w-full px-4 py-2 border border-gray-200 rounded-lg"
            />
            <input
              type="number"
              value={rangeEnd}
              onChange={(e) => setRangeEnd(e.target.value)}
              placeholder="End value"
              className="w-full px-4 py-2 border border-gray-200 rounded-lg"
            />
            <input
              type="number"
              value={rangeSteps}
              onChange={(e) => setRangeSteps(e.target.value)}
              placeholder="Number of steps (max 20)"
              min="2"
              max="20"
              className="w-full px-4 py-2 border border-gray-200 rounded-lg"
            />
            <select
              value={rangeUnit}
              onChange={(e) => setRangeUnit(e.target.value)}
              className="w-full px-4 py-2 border border-gray-200 rounded-lg"
            >
              <option value="">Select unit</option>
              {unitsByCategory[category].map(unit => (
                <option key={unit.value} value={unit.value}>
                  {unit.label}
                </option>
              ))}
            </select>
          </div>
          <button
            type="submit"
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Generate Range
          </button>
        </form>

        {rangeResults.length > 0 && (
          <div className="mt-6 overflow-x-auto">
            <div className="bg-blue-50 p-4 rounded-lg mb-4">
              <p className="text-sm text-blue-800">
                <strong>Tip:</strong> Use this table to:
                <ul className="list-disc ml-4 mt-1 space-y-1">
                  <li>Create reference tables</li>
                  <li>Find equivalent values across units</li>
                  <li>Analyze trends and relationships</li>
                  <li>Educational purposes and visualization</li>
                </ul>
              </p>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="py-2 px-4 text-left font-medium text-gray-600">Value</th>
                  {unitsByCategory[category].map(unit => (
                    <th key={unit.value} className="py-2 px-4 text-left font-medium text-gray-600">
                      {unit.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rangeResults.map((result, index) => (
                  <tr key={index} className="border-b border-gray-100">
                    <td className="py-2 px-4 font-medium">{result.value.toFixed(2)}</td>
                    {result.conversions.map((conv, i) => (
                      <td key={i} className="py-2 px-4">{conv.value.toFixed(4)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};